import _, {concat, isUndefined} from 'lodash';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownItemChannelIcon} from '../../../components/common/dropdowns/dropdownItemChannelIcon';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {
  FilterRowGenerator,
  ResourceMultiSelectFilterDropdown,
} from '../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../../components/common/filters/sourceFilter/filterDropdownSource';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsChannelModel} from '../../../models/api/analyticsFilterResourcesModel';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {
  getAccessibleSharedChannels,
  getUserPrivateChannels,
} from '../../../store/data/channels/channelsSelectors';
import {getCompany} from '../../../store/data/company/companySelectors';
import {
  getAnalyticsFilterResourcesChannels,
  getSelectedTeamNamespaces,
} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {getAnalyticsViewsAllChannels} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {makeChannelsFetcher} from '../../helpers/analyticsInboxFilterHelpers';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.CHANNELS];

/*
 * Styles.
 */

const StyledChannelItemWrapper = styled.div`
  ${flexCenter}
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

/*
 * Hook.
 */

export const useAnalyticsChannelsFilter = (): AnalyticsFilterItem => {
  const [selectedChannelIds, onUpdateFilter] = useAnalyticsFilter(AnalyticsFilterTypesEnum.CHANNELS);
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.CHANNELS);
  const selectedTeamNamespaces = useAppSelector(getSelectedTeamNamespaces);
  const company = useAppSelector(getCompany);

  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const isIndividualReport = isTeammateNamespace(namespace);

  const sharedChannels = useAppSelector(getAccessibleSharedChannels);
  const privateChannels = useAppSelector(getUserPrivateChannels);
  const savedViewsMetadataChannels = useAppSelector(getAnalyticsViewsAllChannels);
  const channelsFetchedForFilters = useAppSelector(getAnalyticsFilterResourcesChannels);

  const reduxChannels = concat(sharedChannels, privateChannels);

  const theme = useUserTheme();

  // Add the channels from redux when looking at the "Me" namespace, otherwise just use the channels coming from the saved view's metadata.
  const localChannelsMap = useMemo(
    () =>
      _(isIndividualReport ? reduxChannels : [])
        .uniqBy((channel) => channel.id)
        .sortBy((channel) => channel.displayName.toLowerCase())
        .keyBy((channel) => channel.id)
        .merge(savedViewsMetadataChannels)
        .merge(channelsFetchedForFilters)
        .value(),
    [savedViewsMetadataChannels, isIndividualReport, reduxChannels, channelsFetchedForFilters],
  );
  const [fetchedChannels, setFetchedChannels] = useState<ReadonlyArray<AnalyticsChannelModel>>([]);

  // Merge of channels from redux, from the saved views metadata and from the filter fetcher results.
  const channelsMap = useMemo(
    () => _(fetchedChannels).keyBy('id').merge(localChannelsMap).value(),
    [fetchedChannels, localChannelsMap],
  );

  useEffect(() => {
    if (!selectedChannelIds) {
      return;
    }

    const updatedSelection = selectedChannelIds.filter((channelId) => {
      const channel = localChannelsMap[channelId];

      if (!channel) {
        return true;
      }

      if (selectedTeamNamespaces.size === 0 || selectedTeamNamespaces.has(channel.namespace)) {
        return true;
      }

      return false;
    });

    if (updatedSelection.length !== selectedChannelIds.length) {
      onUpdateFilter(updatedSelection);
    }
  }, [localChannelsMap, selectedChannelIds, selectedTeamNamespaces, onUpdateFilter]);

  const onSelectItems = (updatedSelectedItems: ReadonlyArray<number>) => {
    onUpdateFilter(updatedSelectedItems);
    trackOnEdit(updatedSelectedItems, selectedChannelIds);
  };

  const isActive = !isUndefined(selectedChannelIds) && selectedChannelIds.length > 0;

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedChannelIds}
      itemsMap={channelsMap}
      iconName={isActive ? activeIconName : iconName}
      renderItem={renderButtonItem}
      label={name}
      isActive={isActive}
      shouldLabelSelection={false}
    />
  );

  const onChannelsFetched = (channels: ReadonlyArray<AnalyticsChannelModel>) => {
    if (channels.length === 0) {
      return;
    }

    setFetchedChannels((channelsState) => channelsState.concat(channels));
  };

  const analyticsChannelsFetcher = makeChannelsFetcher(
    company,
    namespace,
    selectedTeamNamespaces,
    onChannelsFetched,
  );

  const dropdownSource = computeFilterDropdownSourceAsync(analyticsChannelsFetcher);

  const renderDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <ResourceMultiSelectFilterDropdown
      {...dropdownProps}
      dropdownTitle={name}
      selectedItemIds={selectedChannelIds}
      onSetItems={onSelectItems}
      dropdownSource={dropdownSource}
      computeFilterGenerator={computeFilterGenerator}
      onClearAll={trackOnRemove}
    />
  );

  const computeFilterGenerator: FilterRowGenerator<AnalyticsChannelModel> = (
    item,
    dropdownSelectedIds,
    makeOnItemSelect,
  ) => {
    const isChecked = dropdownSelectedIds.includes(item.id);

    return {
      renderer: () => (
        <>
          <DropdownItemChannelIcon channel={item} />

          <DropdownItemContent isChecked={isChecked}>
            <StyledChannelItemWrapper>
              {item.displayName}
              {isTeammateNamespace(item.namespace) && (
                <StyledIcon name="lockFilledTiny" color={theme.greys.shade70} />
              )}
            </StyledChannelItemWrapper>
          </DropdownItemContent>
        </>
      ),
      onSelect: makeOnItemSelect(item.id),
      isSelected: isChecked,
    };
  };

  return {
    id: 'channels-filter',
    type: AnalyticsFilterTypesEnum.CHANNELS,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

const renderButtonItem = (channel: AnalyticsChannelModel) => (
  <Fragment key={channel.id}>{channel.displayName}</Fragment>
);
